import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Star, Quote, PenLine } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const Stars = ({ rating, size = 15, interactive = false, onPick }) => (
  <div className="flex gap-1" data-testid={interactive ? "review-star-picker" : undefined}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type={interactive ? "button" : undefined}
        onClick={interactive ? () => onPick(n) : undefined}
        className={interactive ? "hover:scale-125 transition-transform cursor-pointer" : "cursor-default"}
        data-testid={interactive ? `star-${n}` : undefined}
        aria-label={interactive ? `${n} star` : undefined}
      >
        <Star size={size} className={n <= rating ? "fill-[#C5A059] text-[#C5A059]" : "text-[#0D1B2A]/20"} />
      </button>
    ))}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [sending, setSending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/reviews").then((res) => setReviews(res.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please add your name and a few words about your stay.");
      return;
    }
    setSending(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you for your feedback — we read every review!");
      setForm({ name: "", rating: 5, text: "" });
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not submit review"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "bg-[#FAF8F5] border border-[#0D1B2A]/20 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30 rounded-xl px-4 py-3 text-[#0D1B2A] placeholder:text-gray-400 outline-none w-full transition-all text-sm";

  return (
    <section id="reviews" className="bg-[#EFEBE1] py-20 md:py-28" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeading eyebrow="Guest Stories" title="What Our Guests Say" testId="reviews" />
          <Reveal delay={0.1}>
            <button
              onClick={() => setFormOpen(!formOpen)}
              className="flex items-center gap-2 bg-[#0A192F] text-[#F8F6F0] hover:bg-[#C5A059] hover:text-[#0A192F] rounded-full px-6 py-3 text-sm font-semibold transition-all"
              data-testid="leave-review-button"
            >
              <PenLine size={15} /> Leave a Review
            </button>
          </Reveal>
        </div>

        {formOpen && (
          <motion.form
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={submit}
            className="bg-[#0A192F] rounded-2xl p-6 sm:p-8 mb-10 space-y-4"
            data-testid="review-form"
          >
            <div className="flex flex-wrap items-center gap-6">
              <input placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} flex-1 min-w-[200px]`} data-testid="review-name-input" />
              <div className="flex items-center gap-3">
                <span className="text-[#F8F6F0]/60 text-xs uppercase tracking-widest font-semibold">Your rating</span>
                <Stars rating={form.rating} size={24} interactive onPick={(n) => setForm({ ...form, rating: n })} />
              </div>
            </div>
            <textarea placeholder="Tell us about your stay — the room, the lake, the town…" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} className={inputCls} data-testid="review-text-input" />
            <button type="submit" disabled={sending} className="bg-[#C5A059] text-[#0A192F] hover:bg-[#D4AF37] rounded-full px-8 py-3 font-semibold text-sm transition-all disabled:opacity-60" data-testid="review-submit-button">
              {sending ? "Sending…" : "Submit Review"}
            </button>
          </motion.form>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={(i % 3) * 0.08}>
              <div className="bg-[#FAF8F5] border border-[#0D1B2A]/10 rounded-2xl p-6 sm:p-7 h-full hover:border-[#C5A059]/40 hover:shadow-lg transition-all duration-300" data-testid={`review-card-${r.id}`}>
                <Quote size={22} className="text-[#C5A059] mb-4" />
                <p className="text-[#0D1B2A] text-sm sm:text-base leading-relaxed">“{r.text}”</p>
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#0D1B2A]/10">
                  <span className="font-serif-display font-semibold text-[#0A192F]">{r.name}</span>
                  <Stars rating={r.rating} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        {reviews.length === 0 && (
          <p className="text-center text-[#4A5568] text-sm" data-testid="reviews-empty">Be the first to share your stay.</p>
        )}
      </div>
    </section>
  );
};
